import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CONTENT } from "@/content";
import { errorText, fileUrl, useApi } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const A = CONTENT.admin;

export const ImageUpload = ({ label, value, onChange, testId }) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { onChange((await api("post", "/admin/upload", fd)).path); }
    catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      {value && <img src={fileUrl(value)} alt="" className="image-preview" data-testid={`${testId}-preview`} />}
      <div className="row-actions">
        <input type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} disabled={busy} data-testid={`${testId}-input`} />
        {value && <button type="button" className="nb-btn ghost small" onClick={() => onChange("")} data-testid={`${testId}-remove`}>{A.removeImage}</button>}
      </div>
      {busy && <span className="muted">{A.uploading}</span>}
    </div>
  );
};

const WordPicker = ({ field: [key, label, autoLabel], value, onChange }) => {
  const api = useApi();
  const [items, setItems] = useState([]);
  useEffect(() => { api("get", "/admin/content").then((all) => setItems(all.filter((i) => i.status === "published"))).catch(() => {}); }, [api]);
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <select className="nb-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`setting-${key}-select`}>
        <option value="">{autoLabel}</option>
        {items.map((i) => <option key={i.id} value={i.id}>{i.title} ({i.verse_reference})</option>)}
      </select>
    </label>
  );
};

export const SettingsForm = ({ section, form }) => {
  const api = useApi();
  const { settings, reload } = useSettings();
  const [values, setValues] = useState(settings[section]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValues(settings[section]); }, [settings, section]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api("put", `/admin/settings/${section}`, values); await reload(); toast.success(A.settingsSaved); }
    catch (err) { toast.error(errorText(err)); }
    finally { setSaving(false); }
  };

  return (
    <form className="nb-card content-form" onSubmit={save} data-testid={`settings-form-${section}`}>
      <h2 className="admin-h2">{form.title}</h2>
      {form.fields.map(([key, label, multi]) => (
        <label key={key} className="field">
          <span className="field-label">{label}</span>
          {multi
            ? <textarea className="nb-input" rows={4} value={values[key]} onChange={(e) => setValues({ ...values, [key]: e.target.value })} data-testid={`setting-${key}-input`} />
            : <input className="nb-input" value={values[key]} onChange={(e) => setValues({ ...values, [key]: e.target.value })} data-testid={`setting-${key}-input`} />}
        </label>
      ))}
      {form.wordField && (
        <WordPicker field={form.wordField} value={values[form.wordField[0]]} onChange={(v) => setValues({ ...values, [form.wordField[0]]: v })} />
      )}
      {form.imageField && (
        <ImageUpload label={form.imageField[1]} value={values[form.imageField[0]]} onChange={(v) => setValues({ ...values, [form.imageField[0]]: v })} testId={`setting-${form.imageField[0]}`} />
      )}
      <div className="row-actions"><button type="submit" className="nb-btn" disabled={saving} data-testid={`settings-save-${section}`}>{saving ? A.saving : A.save}</button></div>
    </form>
  );
};
